<template>
  <div class="tool-call">
    <BaseButton
      kind="auxiliary"
      :text="normalized.kind === 'ask_user' ? 'askUser' : normalized.name"
      class="tool-call-toggle"
      @click="expanded = !expanded"
    >
      <template #icon>
        <ChevronRightIcon
          class="tool-call-chevron"
          :class="{ expanded: expanded }"
        />
      </template>
    </BaseButton>
    <div v-if="expanded" class="tool-call-details">
      <template v-if="normalized.kind === 'ask_user'">
        <div
          v-for="(aq, i) in normalized.answeredQuestions"
          :key="i"
          class="tool-call-section"
        >
          <span class="tool-call-label">{{ aq.question.short }}</span>
          <Composer
            v-if="aq.question.long && workspaceId"
            class="tool-call-long"
            :document="aq.question.long"
            :workspace-id="workspaceId"
            :editable="false"
            :mentions-enabled="false"
          />
          <pre class="tool-call-pre">{{ formatAnswers(aq.answers) }}</pre>
        </div>
        <template v-if="normalized.conversation && organizationId">
          <BaseButton
            kind="auxiliary"
            :text="convOpen ? 'Conversation' : 'View conversation'"
            class="tool-call-toggle"
            @click="toggleConversation"
          >
            <template #icon>
              <ChevronRightIcon
                class="tool-call-chevron"
                :class="{ expanded: convOpen }"
              />
            </template>
          </BaseButton>
          <ConversationHistory
            v-if="convOpen"
            :messages="conversationHistory"
          />
        </template>
      </template>
      <template v-else>
        <div class="tool-call-section">
          <span class="tool-call-label">Arguments</span>
          <pre class="tool-call-pre">{{
            formatJson(normalized.arguments)
          }}</pre>
        </div>
        <div class="tool-call-section">
          <span class="tool-call-label">Result</span>
          <pre class="tool-call-pre">{{ normalized.result }}</pre>
        </div>
      </template>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, ref, type PropType } from "vue";

import type {
  AiAutomationRunToolCall,
  OpenaiMessage,
  OrgId,
  WorkspaceId,
} from "@ctrl/controller-rpc";

import { normalizeToolCall } from "@/components/ai/automationRunToolCall";
import ConversationHistory from "@/components/ai/ConversationHistory.vue";
import BaseButton from "@/components/base/BaseButton.vue";
import Composer from "@/components/editor/Composer.vue";
import ChevronRightIcon from "@/components/icons/ChevronRightIcon.vue";
import * as inject from "@/components/inject";

export default defineComponent({
  name: "AutomationRunToolCall",
  components: {
    BaseButton,
    ChevronRightIcon,
    Composer,
    ConversationHistory,
  },
  props: {
    call: {
      type: Object as PropType<AiAutomationRunToolCall>,
      required: true,
    },
    organizationId: {
      type: String as PropType<OrgId>,
      default: undefined,
    },
    workspaceId: {
      type: String as PropType<WorkspaceId>,
      default: undefined,
    },
  },
  setup(props) {
    const controller = inject.controller();
    const expanded = ref(false);
    const convOpen = ref(false);
    const conversationHistory = ref<Array<OpenaiMessage>>([]);

    const normalized = computed(() => normalizeToolCall(props.call));

    const toggleConversation = async () => {
      convOpen.value = !convOpen.value;
      const call = normalized.value;
      if (
        !convOpen.value ||
        conversationHistory.value.length > 0 ||
        call.kind !== "ask_user" ||
        !call.conversation ||
        !props.organizationId
      )
        return;
      const conv = await controller.ai.agent.conversation.get(
        props.organizationId,
        call.conversation
      );
      conversationHistory.value = conv.value?.history ?? [];
      conv.release?.();
    };

    const formatJson = (value: unknown): string => {
      try {
        return JSON.stringify(value, null, 2);
      } catch {
        return String(value);
      }
    };

    const formatAnswers = (answers: string[]): string =>
      answers.length > 0 ? answers.join("\n") : "(no answer)";

    return {
      normalized,
      expanded,
      convOpen,
      conversationHistory,
      toggleConversation,
      formatJson,
      formatAnswers,
    };
  },
});
</script>

<style scoped lang="scss">
.tool-call {
  display: flex;
  flex-direction: column;
}

.tool-call-toggle {
  width: 100%;
}

.tool-call-chevron {
  flex-shrink: 0;
  width: 14px;
  height: 14px;
  @include transition(transform 0.15s ease-out);

  &.expanded {
    transform: rotate(90deg);
  }
}

.tool-call-details {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 4px;
  margin-inline-start: 20px;
}

.tool-call-section {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.tool-call-label {
  @extend %font-caption-regular;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.05em;

  @include themed() {
    color: t($grey-40);
  }
}

.tool-call-long {
  @extend %font-body-regular;

  @include themed() {
    color: t($grey-70);
  }
}

.tool-call-pre {
  font-family: $mono;
  font-size: 12px;
  white-space: pre-wrap;
  word-break: break-word;
  margin: 0;
  padding: 8px 10px;
  border-radius: $border-radius-s;
  user-select: text;
  cursor: text;

  &::selection,
  *::selection {
    @include themed() {
      background-color: t($tertiary-20);
    }
  }

  @include themed() {
    background-color: t($bg-alt);
    color: t($grey-80);
  }
}
</style>
